import { useRef, useState } from "react";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Trash2, Upload } from "lucide-react";

const DOC_TYPES = [
  { value: "pan_card", label: "PAN Card" },
  { value: "gst_certificate", label: "GST Certificate" },
  { value: "cancelled_cheque", label: "Cancelled Cheque" },
  { value: "agreement_other", label: "Agreement/Other" },
];

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

// Vendor Master Documents (Aug 2026). `vendorCode` present (Edit) ->
// uploads happen immediately via the per-vendor endpoint. `vendorCode`
// absent (Add) -> files are staged locally and handed back via
// `onStagedChange`; the caller uploads them right after the vendor is
// created (see VendorFormDialog's submit flow).
export default function VendorDocumentsSection({ vendorCode, documents, canManage, onUploaded, onDeleted, staged, onStagedChange }) {
  const [docType, setDocType] = useState(DOC_TYPES[0].value);
  const [uploading, setUploading] = useState(false);
  const [busyDocId, setBusyDocId] = useState(null);
  const fileInputRef = useRef(null);

  const pickFile = () => fileInputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!["application/pdf", "image/jpeg", "image/jpg", "image/png"].includes(file.type)) {
      return toast.error("Only PDF, JPG and PNG files are allowed");
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return toast.error("File must be under 5MB");
    }
    if (!vendorCode) {
      onStagedChange([...staged, { key: `${Date.now()}-${file.name}`, file, doc_type: docType }]);
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("doc_type", docType);
    fd.append("file", file);
    try {
      await client.post(`/vendor-master/vendors/${vendorCode}/documents`, fd);
      toast.success("Document uploaded");
      onUploaded();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  const viewDocument = async (doc) => {
    try {
      const res = await client.get(`/vendor-master/vendors/${vendorCode}/documents/${doc.doc_id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const deleteDocument = async (doc) => {
    if (!window.confirm(`Delete "${doc.filename}"? This cannot be undone.`)) return;
    setBusyDocId(doc.doc_id);
    try {
      await client.delete(`/vendor-master/vendors/${vendorCode}/documents/${doc.doc_id}`);
      toast.success("Document deleted");
      onDeleted();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyDocId(null);
    }
  };

  const removeStaged = (key) => onStagedChange(staged.filter((s) => s.key !== key));
  const docLabel = (v) => DOC_TYPES.find((d) => d.value === v)?.label || v;

  return (
    <div className="border-t border-slate-200 pt-4" data-testid="vendor-documents-section">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Documents</p>

      {canManage && (
        <div className="flex items-center gap-2 mb-3">
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger data-testid="vendor-doc-type-select" className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DOC_TYPES.map((d) => (
                <SelectItem key={d.value} value={d.value} data-testid={`vendor-doc-type-option-${d.value}`}>{d.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button type="button" variant="outline" size="sm" onClick={pickFile} disabled={uploading} data-testid="vendor-doc-upload-button">
            <Upload className="h-3.5 w-3.5 mr-1.5" /> {uploading ? "Uploading..." : "Upload document"}
          </Button>
          <input ref={fileInputRef} type="file" hidden accept=".pdf,.jpg,.jpeg,.png" onChange={handleFile} data-testid="vendor-doc-file-input" />
        </div>
      )}

      <div className="space-y-1.5">
        {documents.length === 0 && staged.length === 0 && (
          <p className="text-xs text-slate-400" data-testid="vendor-doc-empty">No documents uploaded.</p>
        )}
        {documents.map((doc) => (
          <div key={doc.doc_id} className="flex items-center justify-between gap-2 text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2" data-testid={`vendor-doc-row-${doc.doc_id}`}>
            <button type="button" onClick={() => viewDocument(doc)} className="flex items-center gap-2 text-left text-amber-700 hover:underline min-w-0" data-testid={`vendor-doc-view-${doc.doc_id}`}>
              <FileText className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">
                <span className="font-medium">{docLabel(doc.doc_type)}</span> — {doc.filename}
              </span>
            </button>
            <div className="flex items-center gap-2 flex-shrink-0 text-slate-400">
              <span>{doc.uploaded_by_name || doc.uploaded_by} · {new Date(doc.uploaded_at).toLocaleDateString("en-IN")}</span>
              {canManage && (
                <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" disabled={busyDocId === doc.doc_id} onClick={() => deleteDocument(doc)} data-testid={`vendor-doc-delete-${doc.doc_id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        ))}
        {staged.map((s) => (
          <div key={s.key} className="flex items-center justify-between gap-2 text-xs bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid={`vendor-doc-staged-${s.key}`}>
            <span className="flex items-center gap-2 min-w-0">
              <FileText className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate"><span className="font-medium">{docLabel(s.doc_type)}</span> — {s.file.name}</span>
            </span>
            <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={() => removeStaged(s.key)} data-testid={`vendor-doc-remove-staged-${s.key}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
